/**
 * A setting on the pause screen with more than two states.
 *
 * The sibling of OverlayToggle, and it exists because language is not a
 * yes-or-no question. Written as a checkbox it read "Language: English" with
 * a tick beside it, and nothing on the screen said what unticking would do -
 * you had to try it to find out. A list of the options, with the current one
 * showing, answers that before it is asked.
 *
 * It needs the same three careful bits the toggle does: read the saved value
 * without letting blocked storage break the game, write it back the same way,
 * and stop clicks reaching the overlay, which turns any click into "start
 * playing" and would begin the game the moment you reached for the control.
 *
 * The elements are handed in rather than looked up here, for the reason
 * OverlayToggle gives: the style guard finds the ids a UI asks for by reading
 * `getElementById("...")` out of the source, and taking them as strings would
 * hide them from it.
 */

export class OverlaySelect<T extends string> {
  private current: T;

  constructor(
    private readonly el: HTMLSelectElement,
    /** The row around it. Its clicks must not reach the overlay. */
    row: HTMLElement,
    private readonly storageKey: string,
    /** The options, in the order they are shown. */
    options: readonly { readonly value: T; readonly label: string }[],
    /** Turn what was stored - or nothing - into a value. Must accept null. */
    parse: (raw: string | null) => T,
    /** Runs after every change. */
    private readonly onChange: () => void,
  ) {
    /*
     * The options are built here, from the caller's list.
     *
     * Not written into index.html: the names of the languages already exist
     * in core/text, and two copies of that list would eventually disagree -
     * with the markup winning, silently, because it is what the browser draws.
     */
    this.setOptions(options);

    let saved: string | null = null;
    try {
      saved = localStorage.getItem(storageKey);
    } catch {
      // Private mode or blocked storage. Whatever parse() makes of null is
      // the default, and every setting here has a safe one.
    }
    this.current = parse(saved);
    el.value = this.current;

    el.addEventListener("change", () => {
      this.set(el.value as T);
    });
    // The overlay turns any click into a request for pointer lock, which
    // would start the game the moment you reached for the list.
    row.addEventListener("click", (e) => e.stopPropagation());
    el.addEventListener("mousedown", (e) => e.stopPropagation());
  }

  /**
   * Write the options again, in whatever language is now in force.
   *
   * Set once at construction, they stayed in the language the page was built
   * in - the map list still read "Запалити кремлін" in an English game, which
   * is the same fault the save panel had and was caught the same way. The
   * chosen value is kept across the rewrite.
   */
  setOptions(options: readonly { readonly value: T; readonly label: string }[]): void {
    const keep = this.el.value;
    this.el.replaceChildren(...options.map((option) => {
      const node = document.createElement("option");
      node.value = option.value;
      node.textContent = option.label;
      return node;
    }));
    if (options.some((o) => o.value === keep)) this.el.value = keep;
  }

  get value(): T {
    return this.current;
  }

  /**
   * Choose from outside, as though somebody had picked it.
   *
   * Used by the save panel: pressing «Грати» on a map's world is the same
   * act as picking that map here and carrying on, and doing it through the
   * picker keeps one path into a world instead of two.
   */
  choose(value: T): void {
    this.set(value);
  }

  private set(value: T): void {
    this.current = value;
    this.el.value = value;
    try {
      localStorage.setItem(this.storageKey, value);
    } catch {
      // Not remembering the choice is survivable; refusing it is not.
    }
    this.onChange();
  }
}
